import * as React from 'react';
import Area from './Area';
import { Canvas } from '@react-three/fiber';
import { View } from '@react-three/drei';
import { create } from 'zustand';
import { Menu, Button, MantineProvider } from '@mantine/core';
import {
  IconPerspective,
  IconPerspectiveOff,
  IconArrowBigUp,
  IconArrowBigLeft,
  IconArrowBigRight,
  IconArrowBigDown,
  IconHomeUp,
  IconHomeDown,
} from '@tabler/icons-react';

export type Props = {};

type Projection = 'Perspective' | 'Orthographic';
type Side = 'Top' | 'Bottom' | 'Left' | 'Right' | 'Front' | 'Back';
type Which = 'top' | 'middle' | 'bottom';
type Store = { [which in Which]: Side } & {
  projection: Projection;
  setPanelView: (which: Which, view: Side) => void;
  setProjection: (projection: Projection) => void;
};

const useStore = create<Store>((set) => ({
  projection: 'Perspective',
  top: 'Back',
  middle: 'Top',
  bottom: 'Right',
  setPanelView: (which, view) => set({ [which]: view }),
  setProjection: (projection) => set({ projection }),
}));

const MainPanel = React.forwardRef<HTMLDivElement, React.ComponentPropsWithRef<'div'>>(({ ...props }, ref) => {
  const projection = useStore((state) => state.projection);
  const setProjection = useStore((state) => state.setProjection);
  return (
    <div ref={ref} className="panel" style={{ gridArea: 'main' }} {...props}>
      {/* <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>{children}</View> */}
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Button>{projection}</Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconPerspective size={14} />} onClick={() => setProjection('Perspective')}>
            Perspective
          </Menu.Item>
          <Menu.Item leftSection={<IconPerspectiveOff size={14} />} onClick={() => setProjection('Orthographic')}>
            Orthographic
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
});

const SidePanel = React.forwardRef<HTMLDivElement, { which: Which }>(({ which }, ref) => {
  const value = useStore((state) => state[which]);
  const setPanelView = useStore((state) => state.setPanelView);
  // console.log('SidePanel', { which, value });
  return (
    <div ref={ref} className="panel" style={{ gridArea: which }}>
      {/* <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>{children}</View> */}
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Button>{value}</Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconArrowBigUp size={14} />} onClick={() => setPanelView(which, 'Top')}>
            Top
          </Menu.Item>
          <Menu.Item leftSection={<IconArrowBigDown size={14} />} onClick={() => setPanelView(which, 'Bottom')}>
            Bottom
          </Menu.Item>
          <Menu.Item leftSection={<IconArrowBigLeft size={14} />} onClick={() => setPanelView(which, 'Left')}>
            Left
          </Menu.Item>
          <Menu.Item leftSection={<IconArrowBigRight size={14} />} onClick={() => setPanelView(which, 'Right')}>
            Right
          </Menu.Item>
          <Menu.Item leftSection={<IconHomeUp size={14} />} onClick={() => setPanelView(which, 'Front')}>
            Front
          </Menu.Item>
          <Menu.Item leftSection={<IconHomeDown size={14} />} onClick={() => setPanelView(which, 'Back')}>
            Back
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
});

function App01(props: Props) {
  const {} = props;
  return (
    <MantineProvider>
      <Area className="relative">
        {() => (
          <>
            <div className="absolute">
              <Canvas className="top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <View.Port />
              </Canvas>
            </div>
            <div className="top-0 left-0 grid grid-cols-2 grid-rows-3 w-full h-full gap-2">
              <div className="border border-red-500 row-span-3 col-start-1 row-start-1">
                <MainPanel></MainPanel>
              </div>
              <div className="border border-red-500 col-start-2 row-start-1">
                <SidePanel which="top" />
              </div>
              <div className="border border-red-500 col-start-2 row-start-2">
                <SidePanel which="middle" />
              </div>
              <div className="border border-red-500 col-start-2 row-start-3">
                <SidePanel which="bottom" />
              </div>
            </div>
          </>
        )}
      </Area>
    </MantineProvider>
  );
}

export default React.memo(App01);
